import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatTokens, type UsageDTO, type UsageRow } from '@harness/shared';
import { PageHeader, Seg, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { usd } from '../../lib/format';
import { qk } from '../../lib/keys';

function UsageTable({ title, rows }: { title: string; rows: UsageRow[] }) {
  const max = Math.max(...rows.map((r) => r.costUsd), 0);
  const maxTok = Math.max(...rows.map((r) => r.tokensIn + r.tokensOut), 0);
  return (
    <section className="card audit-card">
      <div className="section-title" style={{ padding: '0 12px 4px' }}>
        {title}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: 100 }}>Tokens in</th>
            <th style={{ width: 100 }}>Cached in</th>
            <th style={{ width: 100 }}>Tokens out</th>
            <th style={{ width: 70 }}>Turns</th>
            <th style={{ width: 90 }}>Est. cost</th>
            <th style={{ width: '22%' }} aria-label="Share" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // Bars show cost share; for free (self-hosted) rows, token share instead.
            const share = max > 0 ? r.costUsd / max : maxTok > 0 ? (r.tokensIn + r.tokensOut) / maxTok : 0;
            return (
              <tr key={`${r.key}-${r.label}`}>
                <td className="ellipsis" style={{ maxWidth: 380 }} title={r.label}>
                  {r.label}
                </td>
                <td>{formatTokens(r.tokensIn)}</td>
                <td className={r.cachedTokensIn ? undefined : 'faint'}>{r.cachedTokensIn ? formatTokens(r.cachedTokensIn) : '—'}</td>
                <td>{formatTokens(r.tokensOut)}</td>
                <td>{r.turns}</td>
                <td title={r.unpricedTokens ? `${formatTokens(r.unpricedTokens)} tokens are from models with no price set (Providers & models), so this leaves them out` : undefined}>
                  {r.unpricedTokens && !r.costUsd ? <span className="faint">No price</span> : usd(r.costUsd)}
                  {r.unpricedTokens > 0 && r.costUsd > 0 && <span className="faint"> +?</span>}
                </td>
                <td>
                  <div className="bar">
                    <span style={{ width: `${Math.max(2, share * 100)}%` }} />
                  </div>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="muted">
                No usage in this period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export default function UsagePage() {
  const [days, setDays] = useState<'7' | '30' | '90'>('30');
  const q = useQuery({ queryKey: qk.usage(Number(days)), queryFn: () => api.get<UsageDTO>(`/api/usage?days=${days}`) });
  const t = q.data?.total;
  return (
    <div className="settings-content">
      <PageHeader title="Usage" sub="Tokens and estimated cost per project, provider, agent and conversation. Costs use each model’s price profile.">
        <Seg
          value={days}
          onChange={setDays}
          options={[
            { value: '7', label: '7 days' },
            { value: '30', label: '30 days' },
            { value: '90', label: '90 days' },
          ]}
        />
      </PageHeader>
      {q.isLoading || !q.data ? (
        <Spinner />
      ) : (
        <>
          <div className="stat-tiles">
            <div className="tile tile-mint">
              <div className="t">Estimated cost</div>
              <div className="v" style={{ color: 'var(--tile-mint-ink)' }}>{usd(t!.costUsd)}</div>
              {t!.unpricedTokens > 0 && <div className="s">excludes {formatTokens(t!.unpricedTokens)} tokens from models with no price set</div>}
            </div>
            <div className="tile tile-lavender">
              <div className="t">Tokens in</div>
              <div className="v" style={{ color: 'var(--tile-lavender-ink)' }}>{formatTokens(t!.tokensIn)}</div>
              <div className="s">
                {t!.cachedTokensIn ? `${formatTokens(t!.cachedTokensIn)} from cache (${Math.round((t!.cachedTokensIn / Math.max(1, t!.tokensIn)) * 100)}%)` : 'no cache reads reported'}
              </div>
            </div>
            <div className="tile tile-blue">
              <div className="t">Tokens out</div>
              <div className="v" style={{ color: 'var(--tile-blue-ink)' }}>{formatTokens(t!.tokensOut)}</div>
            </div>
            <div className="tile tile-cream">
              <div className="t">Agent turns</div>
              <div className="v" style={{ color: 'var(--tile-cream-ink)' }}>{t!.turns}</div>
            </div>
          </div>
          <UsageTable title="By project" rows={q.data.byProject} />
          <UsageTable title="By provider · model" rows={q.data.byProvider} />
          <UsageTable title="By person" rows={q.data.byPerson} />
          <UsageTable title="By agent" rows={q.data.byAgent} />
          <UsageTable title="Top conversations" rows={q.data.byConversation} />
        </>
      )}
    </div>
  );
}
